import { useState, useMemo } from 'react';
import { ChevronDown, Check, X, Search } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

interface FilterOption {
  value: string;
  label: string;
}

interface SingleSelectFilterProps {
  label: string;
  options: FilterOption[];
  selected: string | null;
  onSelectionChange: (value: string | null) => void;
  searchable?: boolean;
  loading?: boolean;
  searchPlaceholder?: string;
}

export function SingleSelectFilter({
  label,
  options,
  selected,
  onSelectionChange,
  searchable = true,
  loading = false,
  searchPlaceholder = 'Search...',
}: SingleSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const hasSelection = selected !== null;
  const selectedLabel = options.find((o) => o.value === selected)?.label;

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
            hasSelection
              ? 'border-brand-rose/40 bg-brand-rose/5 text-brand-rose'
              : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
          }`}
        >
          {hasSelection ? selectedLabel : label}
          <ChevronDown className="w-3 h-3 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 max-w-[calc(100vw-1rem)] p-0" align="start">
        {searchable && (
          <div className="flex items-center border-b border-slate-200 px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex h-9 w-full bg-transparent py-3 text-sm outline-none placeholder:text-slate-400"
            />
          </div>
        )}
        <div className="max-h-[300px] overflow-y-auto overflow-x-hidden p-1">
          {loading && <p className="py-6 text-center text-sm text-slate-500">Loading...</p>}
          {!loading && filtered.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-500">No results found.</p>
          )}
          {filtered.map((opt) => (
            <button
              key={opt.value}
              type="button"
              title={opt.label}
              onClick={() => {
                onSelectionChange(selected === opt.value ? null : opt.value);
                setOpen(false);
                setSearch('');
              }}
              className="relative flex w-full min-w-0 cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm text-left outline-none hover:bg-slate-100 transition-colors"
            >
              <Check
                className={`mr-2 h-4 w-4 shrink-0 ${selected === opt.value ? 'opacity-100' : 'opacity-0'}`}
              />
              <span className="truncate">{opt.label}</span>
            </button>
          ))}
        </div>
        {hasSelection && (
          <div className="border-t border-slate-200 p-1">
            <button
              type="button"
              onClick={() => { onSelectionChange(null); setOpen(false); setSearch(''); }}
              className="flex items-center gap-1 w-full px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-brand-rose rounded-md hover:bg-slate-50 transition-colors"
            >
              <X className="w-3 h-3" />
              Clear selection
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
